import { useNavigate } from 'react-router-dom';
import { isAdminRole } from '@lombok/shared';
import { useAuth } from '@/auth/AuthContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PaymentTerms, createCustomerSchema } from '@lombok/shared';
import type { CreateCustomerInput } from '@lombok/shared';
import { Button, Card, CardContent } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { BackLink } from '@/components/DetailRow';
import { FORM_COLUMN } from '@/components/layout';
import {
  CheckboxField,
  FormActions,
  FormError,
  SelectField,
  TextField,
  emptyToUndefined,
} from '@/components/form';
import { PAYMENT_TERMS_OPTIONS } from '@/features/admin/labels';
import { DistributorField } from './DistributorField';
import { useCreateCustomer } from '@/data/hooks';
import { decimalOrUndefined } from '@/lib/decimal';


/**
 * Create a customer account (admin). RHF + zodResolver against the shared
 * `createCustomerSchema`. Credit limit is captured in whole USD (the backend
 * converts to minor units). Price list, sales rep, and territory links are
 * optional ids. The backend re-validates and re-authorizes on POST.
 */
export function CustomerCreatePage({ basePath = '/app/admin/clientes' }: { basePath?: string } = {}) {
  const BASE = basePath;
  const navigate = useNavigate();
  const { session } = useAuth();
  // Terms, credit, price list, rep and territory are set by an admin at approval; a sales rep's
  // signup is assigned to them and starts with safe defaults (the API enforces the same).
  const isAdmin = session ? isAdminRole(session.user.role) : false;
  const mutation = useCreateCustomer();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateCustomerInput>({
    resolver: zodResolver(createCustomerSchema),
    defaultValues: {
      name: '',
      rif: '',
      paymentTerms: PaymentTerms.UPFRONT,
      creditLimitUsd: 0,
      priceListId: undefined,
      assignedSalesRepId: undefined,
      territoryId: undefined,
      distributorId: undefined,
      optInPromotions: true,
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, {
      onSuccess: (created) => navigate(`${BASE}/${created.customerId}`),
    });
  });

  return (
    <div className={`flex flex-col gap-6 ${FORM_COLUMN}`}>
      <div className="flex flex-col gap-2">
        <BackLink to={BASE}>Clientes</BackLink>
        <PageHeader title="Nuevo cliente" description="Registra una cuenta de cliente." />
      </div>

      <Card>
        <CardContent className="pt-5">
          <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
            <FormError error={mutation.error} />

            <TextField
              label="Nombre"
              required
              placeholder="Comercial Ejemplo, C.A."
              error={errors.name?.message}
              {...register('name')}
            />
            <TextField
              label="RIF"
              required
              placeholder="J-12345678-9"
              error={errors.rif?.message}
              {...register('rif')}
            />

            {isAdmin ? (
              <>
            <div className="grid gap-5 sm:grid-cols-2">
              <SelectField
                label="Términos de pago"
                options={PAYMENT_TERMS_OPTIONS}
                error={errors.paymentTerms?.message}
                {...register('paymentTerms')}
              />
              <TextField
                label="Límite de crédito (USD)"
                inputMode="decimal"
                placeholder="Ej. 5000,00"
                hint="Monto en dólares. El sistema lo guarda en centavos."
                error={errors.creditLimitUsd?.message}
                {...register('creditLimitUsd', { setValueAs: decimalOrUndefined })}
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <TextField
                label="ID de lista de precios"
                placeholder="Opcional"
                error={errors.priceListId?.message}
                {...register('priceListId', { setValueAs: emptyToUndefined })}
              />
              <TextField
                label="ID de vendedor asignado"
                placeholder="Opcional"
                error={errors.assignedSalesRepId?.message}
                {...register('assignedSalesRepId', { setValueAs: emptyToUndefined })}
              />
            </div>
            <TextField
              label="Territorio"
              placeholder="Opcional"
              error={errors.territoryId?.message}
              {...register('territoryId', { setValueAs: emptyToUndefined })}
            />
            <DistributorField
              value={watch('distributorId') ?? ''}
              onChange={(id) => setValue('distributorId', id || undefined, { shouldDirty: true })}
            />
              </>
            ) : (
              <p className="text-sm text-text-muted">
                La cuenta quedará asignada a ti y pendiente de aprobación. Sube el RIF desde el detalle del
                cliente para que el equipo pueda activarla.
              </p>
            )}

            <CheckboxField
              label="Acepta promociones"
              hint="Recibe notificaciones de promociones y novedades."
              {...register('optInPromotions')}
            />

            <FormActions>
              <Button type="button" variant="outline" onClick={() => navigate(BASE)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Guardando…' : 'Crear cliente'}
              </Button>
            </FormActions>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
